import { Box, Typography, useTheme, Button, InputBase, MenuItem, TextField, Menu } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";

import SearchIcon        from "@mui/icons-material/Search";
import InventoryIcon     from "@mui/icons-material/Inventory";
import DownloadIcon      from "@mui/icons-material/Download";
import FilterListIcon    from "@mui/icons-material/FilterList";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import FormatListIcon    from "@mui/icons-material/FormatListBulleted";

import { PDFDownloadLink }  from "@react-pdf/renderer";
import CurrentStockPdf      from "scenes/reports/pdfForms/inventory/currentStockPdf";
import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import { useStockList, useItemTypeList } from "api/hooks/inventory";

export default function Stock() {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const user               = useSelector((s) => s.authReducer.user);
  const { stockList, isLoadingStockList, setSearch, setSearchFilter } = useStockList(1, business_branch_id);
  const { typeList } = useItemTypeList(1, business_branch_id);

  const [searchContent,    setSearchContent]    = useState("");
  const [exportAnchor,     setExportAnchor]     = useState(null);

  const exportOpen = Boolean(exportAnchor);

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  const menuItemSx = {
    fontSize: "13px", color: textPri,
    borderRadius: "6px", mx: "4px",
    "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" },
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <InventoryIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Item Stock List
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {stockList.length} items in inventory
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button
            onClick={() => navigate("/productLogList", { state: { productList: [] } })}
            startIcon={<FormatListIcon sx={{ fontSize: "16px" }} />}
            sx={{
              fontSize: "13px", fontWeight: 500,
              px: "1rem", py: "0.45rem", borderRadius: "8px",
              border: `1px solid ${border}`, color: textSec,
              backgroundColor: "transparent", textTransform: "none",
              "&:hover": { backgroundColor: "rgba(29,158,117,0.06)", borderColor: accent, color: accent },
            }}
          >
            View All Logs
          </Button>

          <Button
            onClick={(e) => setExportAnchor(e.currentTarget)}
            endIcon={<DownloadIcon sx={{ fontSize: "16px" }} />}
            sx={{
              fontSize: "13px", fontWeight: 500,
              px: "1rem", py: "0.45rem", borderRadius: "8px",
              border: `1px solid ${border}`, color: accent,
              backgroundColor: "rgba(29,158,117,0.06)", textTransform: "none",
              "&:hover": { backgroundColor: "rgba(29,158,117,0.12)" },
            }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={exportOpen}
            onClose={() => setExportAnchor(null)}
            PaperProps={{
              sx: {
                mt: "6px", minWidth: "160px",
                backgroundColor: cardBg,
                border: `1px solid ${border}`,
                borderRadius: "10px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              },
            }}
          >
            <MenuItem onClick={() => setExportAnchor(null)} sx={menuItemSx}>
              <PDFDownloadLink
                document={<CurrentStockPdf viewData={{ report_detail: stockList }} printedBy={`${user.first_name} ${user.last_name}`} />}
                fileName="Item Current Stock Report.pdf"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                Download PDF
              </PDFDownloadLink>
            </MenuItem>
            <MenuItem onClick={() => setExportAnchor(null)} sx={menuItemSx}>
              Download Excel
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ── Filter bar ── */}
      <Box sx={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
        p: "0.75rem 1rem",
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "10px",
      }}>
        {/* Search */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: "6px",
          px: "0.75rem", height: "36px",
          backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f9f9f9",
          border: `1px solid ${border}`,
          borderRadius: "8px", flex: "1 1 200px", maxWidth: "280px",
        }}>
          <SearchIcon sx={{ fontSize: "16px", color: textSec }} />
          <InputBase
            placeholder="Search item name…"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchContent); }}
            sx={{ flex: 1, fontSize: "13px", color: textPri }}
          />
          <Box
            onClick={() => setSearch(searchContent)}
            sx={{
              fontSize: "11px", fontWeight: 500, color: accent,
              cursor: "pointer", px: "4px",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Search
          </Box>
        </Box>

        {/* Category filter */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent }}>
          <FilterListIcon sx={{ fontSize: "16px" }} />
          <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Category
          </Typography>
        </Box>
        <TextField label="Item category" size="small" select
          defaultValue="all"
          onChange={(e) => setSearchFilter(e.target.value)}
          sx={{ ...inputSx, width: "180px" }}>
          <MenuItem value="all">All categories</MenuItem>
          {typeList.map((row) => (
            <MenuItem key={row.id} value={row.id}>{row.category_name}</MenuItem>
          ))}
        </TextField>
      </Box>

      {/* ── Table ── */}
      <ReportTable
        columns={[
          { label: "#",               width: 40 },
          { label: "Item Name" },
          { label: "Description" },
          { label: "Unit" },
          { label: "Available Stock", align: "center" },
          { label: "Actions",         align: "center" },
        ]}
        rows={stockList.filter((r) =>
          r.product_name?.toLowerCase().includes(searchContent.toLowerCase())
        )}
        loading={isLoadingStockList}
        emptyMessage="No stock items found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>{row.product_name}</RCell>
            <RCell>{row.description}</RCell>
            <RCell>{row.unit}</RCell>
            <RCell align="center">
              <Box sx={{
                display: "inline-flex", alignItems: "center",
                px: "10px", py: "3px", borderRadius: "20px",
                backgroundColor: row.available_stock <= 0
                  ? "rgba(226,75,74,0.1)"
                  : "rgba(29,158,117,0.1)",
                color: row.available_stock <= 0 ? "#e24b4a" : "#1D9E75",
                fontSize: "13px", fontWeight: 500,
              }}>
                {row.available_stock}
              </Box>
            </RCell>
            <RCell align="center">
              <Box
                onClick={() => navigate("/productLogList", { state: { productList: [row] } })}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  px: "8px", py: "4px", borderRadius: "6px",
                  border: `1px solid rgba(29,158,117,0.3)`,
                  cursor: "pointer", color: accent,
                  fontSize: "12px", fontWeight: 500,
                  "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" },
                  transition: "all 0.15s",
                }}
              >
                <VisibilityIcon sx={{ fontSize: "14px" }} /> View Log
              </Box>
            </RCell>
          </RRow>
        )}
      />
    </Box>
  );
}